import {
  Component,
  effect,
  ElementRef,
  inject,
  input,
  InputSignal,
  PLATFORM_ID,
  Signal,
  signal,
  viewChild,
  WritableSignal,
} from '@angular/core';
import { MediaBlockService } from '../../services/media-block-service';
import { FormatDurationPipe, TimerPipe } from '@kinomap-dev/ng-ui-lib/core';
import { isPlatformBrowser, TitleCasePipe } from '@angular/common';
import { UpperFirstLetterPipe } from '../../pipes/upper-first-letter-pipe';
import { CreateContentService } from '../../services/create-content.service';
import { FormGroup } from '@angular/forms';
import { VideoCaptureFrame } from '../../services/video-capture-frame';
import { ColorService } from '../../services/color.service';
import { ContentType } from '../../pages/content-creation/content-creation';
import { FileSizePipe } from '../../pipes/file-size.pipe';
import { PlayerService } from '@kinomap-dev/ng-ui-lib/player';
import { IconComponent } from '@kinomap-dev/ng-ui-lib/basics';
import { PlayerControls } from '../player-controls/player-controls';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
@Component({
  selector: 'app-video-uploader',
  imports: [
    TranslateModule,
    IconComponent,
    TitleCasePipe,
    UpperFirstLetterPipe,
    TimerPipe,
    FileSizePipe,
    FormatDurationPipe,
    PlayerControls,
  ],
  templateUrl: './video-uploader.html',
  styleUrl: './video-uploader.scss',
})
export class VideoUploader {
  readonly createContentService: CreateContentService = inject(CreateContentService);
  contentType: InputSignal<ContentType> = input.required<ContentType>();
  mediaBlockService: MediaBlockService = inject(MediaBlockService);
  playerService: PlayerService = inject(PlayerService);
  private readonly videoCaptureFrame: VideoCaptureFrame = inject(VideoCaptureFrame);
  colorService: ColorService = inject(ColorService);
  private readonly translateService: TranslateService = inject(TranslateService);
  inputRef: Signal<ElementRef<HTMLInputElement>> = viewChild.required('videoInput', {
    read: ElementRef<HTMLInputElement>,
  });
  videoPlayer: Signal<ElementRef<HTMLVideoElement> | undefined> = viewChild('videoElement', {
    read: ElementRef<HTMLVideoElement>,
  });

  readonly mediaForm: InputSignal<FormGroup> = input.required<FormGroup>();

  isVideoDragOver = false;
  isBrowser: boolean = isPlatformBrowser(inject(PLATFORM_ID));
  videoThumbnailTimstamp: number = 12;
  thumbnailUrl: Signal<string | undefined> = this.createContentService.videoThumbnailUrl;

  authorizeVideoFormats: string[] = ['mp4', 'mov', 'mpeg'];

  videoDuration: WritableSignal<number | undefined> = signal(undefined);
  isStop: WritableSignal<boolean> = signal(true);
  mouseOut: WritableSignal<boolean> = signal(false);
  private _overlayDelayed: WritableSignal<boolean> = signal(false);
  overlay: Signal<boolean> = this._overlayDelayed.asReadonly();
  private _hideTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    effect(() => {
      const progress: number = this.playerService.progress();
      const video: HTMLVideoElement | undefined = this.videoPlayer()?.nativeElement;
      if (!video || !Number.isFinite(progress)) return;
      const targetTime: number =
        Number.isFinite(video.duration) && video.duration > 0
          ? Math.min(Math.max(progress, 0), Math.max(video.duration - 0.1, 0))
          : Math.max(progress, 0);
      if (Math.abs(video.currentTime - targetTime) < 0.25) return;
      video.currentTime = targetTime;
    });

    effect(() => {
      const videoEl: HTMLVideoElement | undefined = this.videoPlayer()?.nativeElement;
      if (videoEl?.paused || videoEl?.ended) {
        this.isStop.update(() => false);
      } else {
        this.isStop.update(() => true);
      }
    });

    effect(() => {
      const isOut = this.mouseOut();
      if (this._hideTimeout) {
        clearTimeout(this._hideTimeout);
        this._hideTimeout = null;
      }

      if (isOut) {
        this._hideTimeout = setTimeout(() => {
          this._overlayDelayed.set(true);
        }, 2000);
      } else {
        this._overlayDelayed.set(false);
      }
    });
  }

  onRemoveVideo(): void {
    this.mediaBlockService.videoFileData.set(undefined);
    this.mediaBlockService.videoFile.set(undefined);
    this.mediaBlockService.videoUrl.set(undefined);
    this.createContentService.videoThumbnailUrl.set(undefined);
    this.createContentService.videoExtention.set(undefined);
    this.mediaForm().get('videoFile')?.setValue(null);
    this.playerService.updateProgress(0);
    this.mediaBlockService.clearVideoInput();
    this.mediaBlockService.clearSync();
  }

  onVideoDrop(event: DragEvent): void {
    event.preventDefault();
    this.isVideoDragOver = false;
    const files: FileList | null = event.dataTransfer?.files || null;
    if (files && files.length > 0) {
      this.handleVideoFile(files[0], true);
    }
  }

  videoLoaded(event: any): void {
    const video: HTMLVideoElement = event.target;
    const duration = video.duration;
    this.videoDuration.set(duration);
    if (this.mediaBlockService.videoFile) {
      this.mediaBlockService.videoFileData.set({
        name: this.mediaBlockService.videoFile()!.name,
        duration,
        weight: this.mediaBlockService.videoFile()!.size,
      });
    }
  }

  onVideoTimeUpdate(event: Event): void {
    const video: HTMLVideoElement | null = event.target as HTMLVideoElement | null;
    if (!video) return;
    this.playerService.updateProgress(video.currentTime || 0);
  }

  async onSelectVideoFile($event: Event): Promise<void> {
    if (!this.isBrowser) return;
    const file: File | undefined = ($event.target as HTMLInputElement).files?.[0];
    if (file) {
      this.handleVideoFile(file, false);
    }
  }

  setIsVideoDragOver(event: DragEvent, value: boolean): void {
    event.preventDefault();
    this.isVideoDragOver = value;
  }

  async onChangeThumbnailClick(): Promise<void> {
    const video: HTMLVideoElement | undefined = this.videoPlayer()?.nativeElement;
    if (!video) {
      console.error('no video element found');
      return;
    }
    this.videoThumbnailTimstamp = video.currentTime;
    await this.captureVideoFrame(this.videoThumbnailTimstamp);
  }

  async captureVideoFrame(timestamp: number): Promise<string | null> {
    if (!this.isBrowser) return null;

    const dataUrl: string | null = await this.videoCaptureFrame.captureFrameUncached(
      timestamp,
      0.95,
    );
    if (dataUrl) {
      this.createContentService.videoThumbnailUrl.set(dataUrl);
      this.createContentService.uploadDate.set(new Date());
    }
    return dataUrl;
  }

  private handleVideoFile(file: File, isDrop: boolean): void {
    const ext: string = file.name.split('.').pop()?.toLowerCase() || '';

    const alertMsg: string = this.translateService.instant(
      isDrop ? 'video_uploader.errors.drop' : 'video_uploader.errors.select',
      { formats: `.${this.authorizeVideoFormats.join(' .').toUpperCase()}` },
    );
    if (this.authorizeVideoFormats.includes(ext)) {
      if (this.mediaBlockService.videoUrl())
        URL.revokeObjectURL(this.mediaBlockService.videoUrl()!);
      this.mediaBlockService.videoFile.set(file);
      this.createContentService.recordDate.set(new Date(file.lastModified));
      this.createContentService.videoOriginalName.set(file.name);
      this.createContentService.videoExtention.set(ext);
      this.mediaForm().get('videoFile')?.setValue(file);
      this.mediaBlockService.videoUrl.set(URL.createObjectURL(file));
      setTimeout(async () => {
        await this.captureVideoFrame(this.videoThumbnailTimstamp);
      }, 100);
    } else {
      alert(alertMsg);
    }
  }

  ngOnInit(): void {
    this.mediaBlockService.inputVideoRefs.set([
      ...this.mediaBlockService.inputVideoRefs(),
      this.inputRef(),
    ]);
  }

  ngOnDestroy(): void {
    const refs: ElementRef<HTMLInputElement>[] = this.mediaBlockService.inputVideoRefs();
    const currentRef: ElementRef<HTMLInputElement> = this.inputRef();
    this.mediaBlockService.inputVideoRefs.set(refs.filter((ref) => ref !== currentRef));
  }
}
